/**
 * What to hide on a native-PMS deployment: screens that are nothing but a
 * window onto MEWS. Everything operational (booking plan, hotel tab,
 * participants, contacts…) stays and is served in MEWS shape by
 * /api/pms/compat — see composables/mewsProxyBasePath.ts.
 *
 * Both helpers return their INPUT untouched when `native` is false, so a
 * MEWS deployment renders exactly what it did before.
 */

/** Left-menu entries (by route path) that only make sense with MEWS. */
export const MEWS_ONLY_MENU_PATHS: ReadonlySet<string> = new Set(['/mews-services', '/pms-blocks'])

/** Property-setup tabs (by id) whose only content is a MEWS import/browser. */
export const MEWS_ONLY_SETUP_TABS: ReadonlySet<string> = new Set(['mews', 'accounting-categories'])

export function hideMewsOnlyMenuItems<T extends { path: string }>(items: T[], native: boolean): T[] {
  return native ? items.filter((i) => !MEWS_ONLY_MENU_PATHS.has(i.path)) : items
}

export function hideMewsOnlySetupTabs<T extends { id: string }>(tabs: T[], native: boolean): T[] {
  return native ? tabs.filter((t) => !MEWS_ONLY_SETUP_TABS.has(t.id)) : tabs
}

/** Left-menu entries that only exist on a native-PMS deployment. */
export const NATIVE_ONLY_MENU_PATHS: ReadonlySet<string> = new Set(['/pms-settings'])

/** Returns the input untouched on native; on MEWS drops the native-only entries. */
export function hideNativeOnlyMenuItems<T extends { path: string }>(items: T[], native: boolean): T[] {
  return native ? items : items.filter((i) => !NATIVE_ONLY_MENU_PATHS.has(i.path))
}

/**
 * /pms-settings is a sectioned page (property, categories + rooms, rates,
 * products, accounting categories, tax codes); entry is granted by ANY of
 * its section keys — the same any-of pattern as property-setup. The page
 * hides the sections the user cannot read.
 */
export const PMS_SETTINGS_ENTRY_KEYS: readonly string[] = [
  'admin-property-settings', 'ps-tab-room-types', 'ps-tab-rates', 'ps-tab-products',
  'ps-tab-accounting-categories', 'ps-tab-tax-codes', 'ps-tab-contacts',
]

export function canEnterPmsSettings(canAccess: (key: string) => boolean): boolean {
  return PMS_SETTINGS_ENTRY_KEYS.some((k) => canAccess(k))
}
